import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, Loader2, ZoomIn, Check, RotateCcw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { cropMedia } from '@/services/api/meta-media';
import type { MediaCrop, CropMediaResponse } from '@/types/media';

// A Meta renderiza o Generic Template em 1,91:1 — é o default de
// `image_aspect_ratio`, campo que o payload nunca define. O que sobrar dessa
// proporção ela corta pelo centro, sem avisar, e junto costuma ir o CTA.
const META_RATIO = 1.91;

// Abaixo disso o recorte sai com menos de 1536px de largura e a arte chega
// menos nítida no Messenger. Não bloqueia — avisa.
const LARGURA_IDEAL = 1536;

interface ImageCropModalProps {
    isOpen: boolean;
    onClose: () => void;
    sourceUrl: string;
    initialCrop?: MediaCrop | null;
    onConfirm: (resultado: CropMediaResponse) => void;
}

interface Ponto {
    x: number;
    y: number;
}

const FUNDOS = [
    { valor: '#FFFFFF', nome: 'Branco' },
    { valor: '#000000', nome: 'Preto' },
    { valor: '#F3F4F6', nome: 'Cinza' },
];

export default function ImageCropModal({
    isOpen,
    onClose,
    sourceUrl,
    initialCrop,
    onConfirm,
}: ImageCropModalProps) {
    const { toast } = useToast();
    const molduraRef = useRef<HTMLDivElement>(null);
    const arrastandoDe = useRef<Ponto | null>(null);
    const offsetAoIniciar = useRef<Ponto>({ x: 0, y: 0 });

    const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
    const [moldura, setMoldura] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState<Ponto>({ x: 0, y: 0 });
    const [fundo, setFundo] = useState('#FFFFFF');
    const [arrastando, setArrastando] = useState(false);

    // Escala em que a arte COBRE a moldura inteira. É a referência do zoom:
    // zoom 1 = sem faixa; abaixo de 1 a arte encolhe e sobra fundo.
    const escalaCobertura = natural && moldura.w
        ? Math.max(moldura.w / natural.w, moldura.h / natural.h)
        : 1;

    // Zoom que faz a arte INTEIRA caber dentro da moldura — o mínimo útil,
    // porque é o enquadramento em que nada da arte é perdido.
    const zoomMinimo = natural && moldura.w
        ? Math.min(moldura.w / natural.w, moldura.h / natural.h) / escalaCobertura
        : 1;

    const escala = escalaCobertura * zoom;
    const exibida = natural
        ? { w: natural.w * escala, h: natural.h * escala }
        : { w: 0, h: 0 };

    const medirMoldura = useCallback(() => {
        const el = molduraRef.current;
        if (!el) return;
        const w = el.clientWidth;
        setMoldura({ w, h: Math.round(w / META_RATIO) });
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        medirMoldura();
        window.addEventListener('resize', medirMoldura);
        return () => window.removeEventListener('resize', medirMoldura);
    }, [isOpen, medirMoldura]);

    useEffect(() => {
        if (!isOpen) {
            setNatural(null);
            setZoom(1);
            setOffset({ x: 0, y: 0 });
        }
    }, [isOpen]);

    // Centraliza (ou reabre no recorte salvo) assim que soubermos as duas
    // medidas: tamanho natural da arte e tamanho da moldura em tela.
    useEffect(() => {
        if (!natural || !moldura.w) return;

        const cobertura = Math.max(moldura.w / natural.w, moldura.h / natural.h);

        if (initialCrop && initialCrop.width > 0) {
            // Desfaz a conta do recorteEmPixels: a moldura corresponde a
            // crop.width px do original, então a escala em tela sai dessa razão.
            const escalaSalva = moldura.w / initialCrop.width;
            setZoom(escalaSalva / cobertura);
            setOffset({
                x: -initialCrop.x * escalaSalva,
                y: -initialCrop.y * escalaSalva,
            });
            if (initialCrop.background) setFundo(initialCrop.background);
            return;
        }

        setZoom(1);
        setOffset({
            x: (moldura.w - natural.w * cobertura) / 2,
            y: (moldura.h - natural.h * cobertura) / 2,
        });
    }, [natural, moldura.w, moldura.h, initialCrop]);

    // Enquanto a arte cobre a moldura, não deixamos abrir fresta: o offset é
    // preso às bordas. Quando ela é menor que a moldura, a faixa é intencional
    // e a arte passeia livre dentro do fundo.
    const limitar = useCallback((p: Ponto): Ponto => {
        if (!natural || !moldura.w) return p;
        const prender = (valor: number, tamanhoExibido: number, tamanhoMoldura: number) => {
            if (tamanhoExibido >= tamanhoMoldura) {
                return Math.min(0, Math.max(tamanhoMoldura - tamanhoExibido, valor));
            }
            return Math.max(0, Math.min(tamanhoMoldura - tamanhoExibido, valor));
        };
        return {
            x: prender(p.x, exibida.w, moldura.w),
            y: prender(p.y, exibida.h, moldura.h),
        };
    }, [natural, moldura.w, moldura.h, exibida.w, exibida.h]);

    useEffect(() => {
        setOffset((atual) => limitar(atual));
    }, [limitar]);

    const aoPressionar = (e: React.PointerEvent) => {
        if (!natural) return;
        e.preventDefault();
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        arrastandoDe.current = { x: e.clientX, y: e.clientY };
        offsetAoIniciar.current = offset;
        setArrastando(true);
    };

    const aoMover = (e: React.PointerEvent) => {
        if (!arrastandoDe.current) return;
        const dx = e.clientX - arrastandoDe.current.x;
        const dy = e.clientY - arrastandoDe.current.y;
        setOffset(limitar({
            x: offsetAoIniciar.current.x + dx,
            y: offsetAoIniciar.current.y + dy,
        }));
    };

    const aoSoltar = (e: React.PointerEvent) => {
        if (!arrastandoDe.current) return;
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        arrastandoDe.current = null;
        setArrastando(false);
    };

    const aplicarZoom = (novoZoom: number) => {
        if (!natural || !moldura.w) return;
        const alvo = Math.min(4, Math.max(zoomMinimo, novoZoom));
        const escalaAtual = escalaCobertura * zoom;
        const escalaNova = escalaCobertura * alvo;
        const centro = { x: moldura.w / 2, y: moldura.h / 2 };
        const razao = escalaNova / escalaAtual;
        setZoom(alvo);
        setOffset(limitar({
            x: centro.x - (centro.x - offset.x) * razao,
            y: centro.y - (centro.y - offset.y) * razao,
        }));
    };

    // Zoom pela roda mantém o centro da moldura parado — sem isso a arte foge
    // da mão do usuário a cada clique da roda.
    const aoRolar = (e: React.WheelEvent) => {
        if (!natural) return;
        aplicarZoom(zoom * (e.deltaY > 0 ? 0.95 : 1.05));
    };

    const centralizar = () => {
        if (!natural || !moldura.w) return;
        setZoom(1);
        setOffset({
            x: (moldura.w - natural.w * escalaCobertura) / 2,
            y: (moldura.h - natural.h * escalaCobertura) / 2,
        });
    };

    // Converte o que está visível na moldura para pixels da imagem ORIGINAL.
    // Guardar em px da arte (e não da tela) é o que permite reabrir o editor
    // no mesmo enquadramento em qualquer monitor.
    const recorteEmPixels = (): MediaCrop & { background: string } => ({
        x: Math.round(-offset.x / escala),
        y: Math.round(-offset.y / escala),
        width: Math.round(moldura.w / escala),
        height: Math.round(moldura.h / escala),
        background: fundo,
    });

    const larguraResultante = natural && escala ? Math.round(moldura.w / escala) : 0;
    const vaiBorrar = larguraResultante > 0 && larguraResultante < LARGURA_IDEAL;
    const temFaixa = natural != null && (exibida.w < moldura.w - 1 || exibida.h < moldura.h - 1);
    const larguraFinal = Math.min(LARGURA_IDEAL, larguraResultante);

    const salvarMutation = useMutation({
        mutationFn: () => cropMedia({ source_url: sourceUrl, ...recorteEmPixels() }),
        onSuccess: (resultado) => {
            onConfirm(resultado);
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao recortar',
                description: error.response?.data?.detail || 'Não foi possível gerar a imagem.',
            });
        },
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900">Ajustar imagem</h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Arraste e use o zoom. O que estiver dentro da moldura é o que a Meta mostra.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={salvarMutation.isPending}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto">
                    <div
                        ref={molduraRef}
                        onPointerDown={aoPressionar}
                        onPointerMove={aoMover}
                        onPointerUp={aoSoltar}
                        onPointerCancel={aoSoltar}
                        onWheel={aoRolar}
                        style={{ aspectRatio: String(META_RATIO), backgroundColor: fundo, touchAction: 'none' }}
                        className={`relative w-full overflow-hidden rounded-lg border-2 border-gray-300 select-none ${arrastando ? 'cursor-grabbing' : 'cursor-grab'}`}
                    >
                        <img
                            src={sourceUrl}
                            alt="Ajuste"
                            draggable={false}
                            onLoad={(e) => {
                                const img = e.currentTarget;
                                setNatural({ w: img.naturalWidth, h: img.naturalHeight });
                            }}
                            style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                width: exibida.w ? `${exibida.w}px` : 'auto',
                                height: exibida.h ? `${exibida.h}px` : 'auto',
                                transform: `translate(${offset.x}px, ${offset.y}px)`,
                                visibility: natural ? 'visible' : 'hidden',
                                maxWidth: 'none',
                                pointerEvents: 'none',
                            }}
                        />

                        {!natural && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                            </div>
                        )}

                        {/* Guia do rodapé: é ali que o CTA costuma morar, e é a faixa
                            que a Meta come quando a arte não está em 1,91:1. */}
                        <div className="absolute inset-0 pointer-events-none">
                            <div className="absolute inset-x-0 bottom-[18%] border-t border-dashed border-white/50" />
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <ZoomIn className="w-4 h-4 text-gray-500 shrink-0" />
                        <Slider
                            value={[zoom]}
                            min={zoomMinimo}
                            max={4}
                            step={0.01}
                            onValueChange={([v]) => aplicarZoom(v)}
                            disabled={!natural}
                            className="flex-1"
                        />
                        <span className="text-xs text-gray-500 w-12 text-right tabular-nums">
                            {Math.round(zoom * 100)}%
                        </span>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={centralizar}
                            disabled={!natural}
                            title="Voltar ao enquadramento cheio"
                        >
                            <RotateCcw className="w-3.5 h-3.5" />
                        </Button>
                    </div>

                    {temFaixa && (
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600">Cor da faixa:</span>
                            {FUNDOS.map((f) => (
                                <button
                                    key={f.valor}
                                    onClick={() => setFundo(f.valor)}
                                    title={f.nome}
                                    className={`w-6 h-6 rounded border-2 transition-colors ${fundo === f.valor ? 'border-cyan-500' : 'border-gray-300'}`}
                                    style={{ backgroundColor: f.valor }}
                                />
                            ))}
                        </div>
                    )}

                    {natural && (
                        <div className="text-xs text-gray-500 space-y-1">
                            <div>
                                Original {natural.w}×{natural.h} · o recorte sai em{' '}
                                {larguraFinal}×{Math.round(larguraFinal / META_RATIO)}
                            </div>
                            {vaiBorrar && (
                                <div className="flex items-start gap-1.5 text-amber-600">
                                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>
                                        Esse enquadramento aproveita menos de {LARGURA_IDEAL}px de largura da
                                        arte — a imagem pode chegar menos nítida. Reduza o zoom ou use uma arte maior.
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
                    <Button variant="outline" onClick={onClose} disabled={salvarMutation.isPending}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => salvarMutation.mutate()}
                        disabled={!natural || salvarMutation.isPending}
                        className="bg-cyan-600 hover:bg-cyan-700"
                    >
                        {salvarMutation.isPending ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Gerando...
                            </>
                        ) : (
                            <>
                                <Check className="w-4 h-4 mr-2" />
                                Usar este recorte
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
